/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/naming-convention */

// Split out of kHTML.ts 2026-10-19 (owner: smaller units for the reuse reviewer). kHTML.ts
// re-exports every class here, so `import { khtmlButton } from "./kHTML"` keeps working.
import ks from "../ReUsableTS/kBrowserShared";
import { kStyleClass, kStyles } from "./kStyles";
import { kFormTheme } from "./kFormTheme";
import type { kIcon } from "./kFluentIcons";
import { kDiv } from "./kHTMLInputs";

export class khtmlButton {
    public Button: HTMLButtonElement = document.createElement("button");
    public Text = "";
    public ClickDelays:boolean = true;
    public ClickCB: any = null;
    // When false, _ButtonClicked does NOT preventDefault/stopPropagation, so the
    // click still bubbles — needed for nav / tab / dot buttons. Default true
    // preserves the original behaviour for every existing caller.
    public SuppressEventDefaults: boolean = true;
    // When false, the button is not disabled-then-re-enabled around the click,
    // so rapid re-clicks (toggles, tab strips) keep working. Default true
    // preserves the original behaviour.
    public DisableOnClick: boolean = true;
    // Captured from the constructor; when false the built-in inline look, the JS
    // hover effect, and the Disable/Enable colour swaps are all skipped so a host
    // stylesheet (e.g. a project's brand CSS) owns the entire appearance.
    private _UseDefaultStyle: boolean = true;
    // ID        — optional element id.
    // ClassName — optional CSS class so a host stylesheet drives the look.
    // UseDefaultStyle — default true keeps the built-in look for every existing
    //             caller; pass false (typically with a ClassName) to opt out.
    public constructor(ID?: string, ClassName?: string, UseDefaultStyle: boolean = true) {
        this.Button = document.createElement("button");
        this.Button.type = "button";
        this._UseDefaultStyle = UseDefaultStyle;

        const kBtnStyleClass:kStyleClass = khtmlButton.DefaultStyle();
        // Assign the host className FIRST (it replaces the class attribute), then
        // let Apply append the built-in style class via classList so the two never
        // clobber each other when both a className and the default style are used.
        if (ks.KNotNullOrUndefined(ClassName)) {
            this.Button.className = ks.Nz(ClassName);
        }
        if (UseDefaultStyle === true) {
            kBtnStyleClass.Apply(this.Button);
        }
        if (ks.KNotNullOrUndefined(ID)) {
            this.Button.id = ks.Nz(ID);
        }
        this.Button.addEventListener("click", this._ButtonClicked);
        this.Button.addEventListener("mouseover", this._HoverIn);
        this.Button.addEventListener("mouseout", this._HoverOut);
    }
    /** The built-in button look as its kStyles class ("kHTMLButton"), configured once. The rule
     *  itself reaches the page's stylesheet when the class is first applied or kStyles.Save runs
     *  (see Preinstall). */
    public static DefaultStyle = (): kStyleClass => {
        if (ks.KErrorMode === true) { return new kStyleClass(); }
        try {
            const kBtnStyleClass:kStyleClass = kStyles.AddStyle("kHTMLButton");
            if(kBtnStyleClass.Saved === true){ return kBtnStyleClass; }
            const ButtonStyle:CSSStyleDeclaration = kBtnStyleClass.Style;
            ButtonStyle.cursor = "pointer";
            ButtonStyle.color = "black";
            ButtonStyle.transitionProperty = "background-color, box-shadow, filter";
            ButtonStyle.transitionDuration = "0.15s";
            ButtonStyle.borderRadius ="5px";
            ButtonStyle.borderWidth = "thin";
            return kBtnStyleClass;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "khtmlButton.DefaultStyle");
            return new kStyleClass();
        }
    };
    /**
     * Put the button's stylesheet rule in the page NOW, without building a button. Later calls
     * and later buttons add nothing.
     *
     * Why (measured 2026-10-01, DocumentBuilder, a 7,600-element editor): the FIRST khtmlButton
     * of a page inserted the `.kHTMLButton` rule inside the click that built it, and a rule
     * added to a live stylesheet makes the browser re-check the whole document against it
     * (3 ms of CPU there, more on a larger page) before anything can paint. An app that calls
     * this at start, while its page is small, pays that once and early. kModalDialog.Preinstall
     * calls it, because every dialog with an OK or Cancel button builds one.
     */
    public static Preinstall = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            khtmlButton.DefaultStyle();
            kStyles.Save();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "khtmlButton.Preinstall");
        }
    };
    public DisableButton = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (null !== this.Button) {
                this.Button.disabled = true;
                if (this._UseDefaultStyle === true) {
                    this.Button.style.backgroundColor = "white";
                    this.Button.style.color = "lightgrey";
                    this.Button.style.cursor = "unset";
                }
                
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "khtmlButton.DisableButton");
        }
    };
    public EnableButton = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (null !== this.Button) {
                this.Button.disabled = false;
                if (this._UseDefaultStyle === true) {
                    this.Button.style.backgroundColor = "unset";
                    this.Button.style.color = "black";
                    this.Button.style.cursor = "pointer";
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "khtmlButton.EnableButton");
        }
    };
    public SetText = (ButtonText: string): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (null !== this.Button) {
                this.Button.textContent = ButtonText;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "khtmlButton.SetText");
        }
    };
    /**
     * Give this button the shared PRIMARY look - filled accent, white text. Use it for
     * the one action that completes the surface (Save, OK, Book).
     *
     * Before this existed, Save and Cancel were rendered identically, so a user had to
     * READ both to find the confirming one. Exactly one primary per surface.
     */
    public SetPrimary = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this._SetVariant("kBtn--primary");
        } catch (e) { ks.KGlobalErrorHandler(e, "khtmlButton.SetPrimary"); }
    };

    /** The shared SECONDARY look - white, bordered. Cancel, Close, Back. */
    public SetSecondary = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this._SetVariant("kBtn--secondary");
        } catch (e) { ks.KGlobalErrorHandler(e, "khtmlButton.SetSecondary"); }
    };

    /** The shared DESTRUCTIVE look - white with a red border and red text. Delete.
        Deliberately NOT a filled red block: a filled destructive button competes with
        the primary action for attention on a form the user is trying to SAVE. */
    public SetDanger = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this._SetVariant("kBtn--danger");
        } catch (e) { ks.KGlobalErrorHandler(e, "khtmlButton.SetDanger"); }
    };

    /**
     * Swap the button onto the shared .kBtn look.
     *
     * Turning _UseDefaultStyle off is LOAD-BEARING, not tidying: DisableButton /
     * EnableButton / _HoverIn write INLINE backgroundColor + color + filter, and an
     * inline style outranks any stylesheet - so a primary button would go white-on-white
     * the first time it was disabled. With the flag off, those writes are skipped and
     * the sheet's :disabled and :hover rules own both states.
     */
    private _SetVariant = (VariantClass: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this._UseDefaultStyle = false;
            this.Button.classList.remove("kHTMLButton");
            this.Button.classList.remove("kBtn--primary");
            this.Button.classList.remove("kBtn--secondary");
            this.Button.classList.remove("kBtn--danger");
            this.Button.style.backgroundColor = "";
            this.Button.style.color = "";
            this.Button.style.filter = "";
            this.Button.style.boxShadow = "";
            kFormTheme.Apply(this.Button, "kBtn");
            this.Button.classList.add(VariantClass);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "khtmlButton._SetVariant");
        }
    };

    private _ButtonClicked = (event:MouseEvent): void => {
        if (ks.KErrorMode === true && ks.KShowingErrorDialog === false) {
            return;
        }
        try {
            if (this.SuppressEventDefaults === true) {
                event.preventDefault();
                event.stopPropagation();
            }
            if (null !== this.ClickCB && null !== this.Button) {
                if (this.DisableOnClick === true) {
                    this.Button.disabled = true;
                }
                ks.DoCallBack(this.ClickCB, undefined, false);
                if (this.DisableOnClick === true) {
                    if(this.ClickDelays){
                        window.setTimeout(this._Enable1sAfterClick, 1000);
                    }else{
                        window.setTimeout(this._Enable1sAfterClick, 10);
                    }
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "khtmlButton._ButtonClicked");
        }
    };
    private _Enable1sAfterClick = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (this.Button) {
                this.Button.disabled = false;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "khtmlButton._Enable1sAfterClick");
        }
    };
    private _HoverIn = (): void => {
        try {
            if (this._UseDefaultStyle === false) { return; }
            if (this.Button && !this.Button.disabled) {
                this.Button.style.filter = "brightness(0.92)";
                this.Button.style.boxShadow = "0 2px 4px rgba(0,0,0,0.12)";
            }
        } catch (_e) { /* ERROR-SUPPRESSED-JUSTIFIED: hover styling is cosmetic; a failure must not surface */ }
    };
    private _HoverOut = (): void => {
        try {
            if (this._UseDefaultStyle === false) { return; }
            if (this.Button) {
                this.Button.style.filter = "";
                this.Button.style.boxShadow = "";
            }
        } catch (_e) { /* ERROR-SUPPRESSED-JUSTIFIED: hover styling is cosmetic; a failure must not surface */ }
    };
    public SetCallBack = (Callback: any): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (null !== this.Button) {
                this.ClickCB = Callback;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "khtmlButton.SetCallBack");
        }
    };
    public SetIcon = (FluentIcon:kIcon): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            FluentIcon.ToButtonIcon(this);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "khtmlButton.SetIcon");
        }
    };
    public SetWidth = (NewWidth:number): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (null !== this.Button) {
                const Widthpx:string = NewWidth +"px";
                const Style:CSSStyleDeclaration = this.Button.style;
                Style.width = Widthpx;
                Style.minWidth = Widthpx;
                Style.maxWidth = Widthpx;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "khtmlButton.SetWidth");
        }
    };
}

export class khtmlUploadButton {
    public UploadWrapper: HTMLDivElement = document.createElement("div");
    public Button: HTMLButtonElement = document.createElement("button");
    public Input: HTMLInputElement = document.createElement("input");
    public Text = "";
    public ClickCB: any = null;
    public constructor(ID?: string,PreventMultiple?:boolean) {
        this.UploadWrapper = new kDiv().Div;
        this.UploadWrapper.style.position = "relative";
        this.UploadWrapper.style.overflow = "hidden";
        this.UploadWrapper.style.display = "inline-block";

        this.Button = document.createElement("button");
        this.Button.type = "button";
        this.Button.style.borderRadius = "5px";
        this.UploadWrapper.appendChild(this.Button);

        this.Input = document.createElement("input");
        this.Input.type = "file";
        if (PreventMultiple !== true) {
            this.Input.multiple = true;
        }
        // Invisible overlay that must make the WHOLE wrapper (button) open the file dialog.
        // A file <input> renders only ~one line tall by default; relying on a huge font-size
        // to stretch it over a tall button is unreliable — a host page (e.g. SharePoint's
        // modern list) can pin `input { height: ... }`, so the input covers only the top strip
        // and clicks lower down hit the dead button underneath (nothing happens). Pin the input
        // to cover the wrapper outright; width/height are marked important so a hostile host
        // stylesheet cannot shrink the clickable area.
        this.Input.style.position = "absolute";
        this.Input.style.left = "0";
        this.Input.style.top = "0";
        this.Input.style.setProperty("width", "100%", "important");
        this.Input.style.setProperty("height", "100%", "important");
        this.Input.style.opacity = "0";
        this.Input.style.cursor = "pointer";
        this.Input.style.boxSizing = "border-box";
        this.UploadWrapper.appendChild(this.Input);
        if (ks.KNotNullOrUndefined(ID)) {
            this.Input.id = ks.Nz(ID);
        }
    }

    public SetText = (ButtonText: string): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (null !== this.Button) {
                this.Button.textContent = ButtonText;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "khtmlUploadButton.SetText");
        }
    };
    public SetCallBack = (Callback: any): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (null !== this.Input) {
                this.Input.addEventListener("change", Callback);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "khtmlUploadButton.SetCallBack");
        }
    };
}

export class kPosition {
    public Top = 0;
    public Left = 0;

    public FromElement(Element: HTMLElement): void {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            const Rect: DOMRect = (Element.getBoundingClientRect() as DOMRect);
            // Make sure element is not hidden (display: none) or disconnected
            if (Rect.width || Rect.height) {
                const doc = Element.ownerDocument;
                if (doc) {
                    const docElem = doc.documentElement;
                    (this.Top = Rect.top + window.scrollY - docElem.clientTop),(this.Left = Rect.left + window.scrollX - docElem.clientLeft);
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kPosition.FromElement");
        }
    }
}
